import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
import { client } from '../../hooks/Client';
import Location from "../../hooks/Location";
import {
  useFirestoreGeneralTwoOrderBy,
  useFirestoreGeneral,
  useFirestoreGeneralOrderBy,
  useFirestoreID
} from "../../firebase/useFirestore";
import { db, timestamp } from "../../firebase/config.js";
import useSettings from "../../hooks/Settings";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import uuid from 'react-uuid';
import ButtonClicked from "../../hooks/ButtonClicked";
import Tooltip from "../../components/common/Tooltip";
import SelectableGrid from "../../components/common/SelectableGrid";
import plusButton from '../../images/icons/plus-icon.png';
import CategoryWords from "../../components/reserachAnalyses/CategoryWords";
import CategoryAnalysisTitle from "../../components/reserachAnalyses/measureMoment/CategoryAnalysisTitle";
import AnalyseResponse from "../../components/reserachAnalyses/measureMoment/AnalyseResponse";
import { Auth } from "../../StateManagment/Auth";
import { ImpactAI } from "../../StateManagment/ImpactAI";
import ScrollToTop from "../../hooks/ScrollToTop";
import PageHeader from "../../components/layout/PageHeader.jsx";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ColorPickerHousestyle from '../../components/common/ColorPickerHousestyle.jsx';
import Table from "../../components/common/table/Table.jsx";
import TableCell from "../../components/common/table/TableCell.jsx";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";

const ResearchResultsOpenQuestionAnalysis = () => {
  // ========== Context & State ==========
  const [saved, setSaved] = useContext(SavedIcon);
  const [auth] = useContext(Auth);
  const {
    type: [type, setType],
    systemMessage: [systemMessage, setSystemMessage],
    toolCallContent: [toolCallContent, setToolCallContent],
    collection: [collection, setCollection],
    field: [field, setField],
    saveMessage: [saveMessage, setSaveMessage],
    startMessage: [startMessage, setStartMessage],
    parentId: [parentId, setParentId],
    exampleMessages: [exampleMessages, setExampleMessages],
    hightlighted: [hightlighted, setHightlighted],
    hightlightText: [hightlightText, setHightlightText],
    chatOpen: [chatOpen, setChatOpen]
  } = useContext(ImpactAI);

  const [categoryID, setCategoryID] = useState('');
  const [color, setColor] = useState('');
  const [momentID, setMomentID] = useState('');
  const [question, setQuestion] = useState('');
  const [resultsArray, setResultsArray] = useState([]);

  // ========== Hooks & Firestore ==========
  ScrollToTop();
  const fieldID    = Location()[3];
  const researchID = Location()[4];
  const secundairyColor = useSettings().SecundairyColor;

  const results    = useFirestoreGeneralTwoOrderBy('QuestionnairesResponses', 'FieldID', fieldID || "", 'MomentID', momentID || "", 'Timestamp', 'desc');
  const categories = useFirestoreGeneral('AnalysisCategories', 'FieldID', fieldID || "");
  const momentsRaw = useFirestoreGeneralOrderBy('MeasureMoments', 'ResearchID', researchID || "", 'Position', 'desc');
  const moments    = useMemo(() => (momentsRaw || []).filter((m) => !m.Deleted), [momentsRaw]);
  const fields     = useFirestoreID('QuestionnaireFields', fieldID || "");

  // ========== ImpactAI Setup ==========
//   useEffect(() => {
//     setType('categorizeResponse');
//     setSystemMessage(ResearchOpenQuestionCategoriesSystemMessage(resultsArray, question));
//     setStartMessage(`Hoi, ${auth?.ForName}. Op deze pagina gaan we de open vraag '${question}' categoriseren zodat we de resultaten kunnen analyseren. Zullen we starten?`);
//     setToolCallContent(ResearchOpenQuestionCategoriesToolCallContent());
//     setSaveMessage(ResearchOpenQuestionCategoriesSaveMessage());
//     setCollection('AnalysisCategories');
//     setField('Categorie');
//     setParentId(fieldID);
//     setExampleMessages([]);
//   }, [auth, resultsArray, question, fieldID]);

  useEffect(() => {
    setHightlighted(false);
    setHightlightText('');
  }, []);

  const impactaiHandler = () => {
    setChatOpen(true);
    setHightlighted(!hightlighted);
    setHightlightText('Ja, laten we starten!');
  };

  // Build resultsArray for AI prompt
  useEffect(() => {
    if (!results) return;
    setResultsArray(results.map(r => r.Input));
  }, [results]);

  // Default moment & question
  useEffect(() => { if (moments?.[0])   setMomentID(moments[0].ID); }, [moments]);
  useEffect(() => { if (fields?.[0])    setQuestion(fields[0].Question); }, [fields]);

  // ========== Handlers ==========
  const selectCategory = (e) => {
    setCategoryID(e.target.value);
    setColor(e.target.dataset.color);
  };

  const deleteCategory = (e) => {
    const docid = e.currentTarget.dataset.docid;
    db.collection('AnalysisCategories').doc(docid).delete();
  };

  const addCategory = (e) => {
    ButtonClicked(e, 'Opgeslagen');
    db.collection('AnalysisCategories').doc().set({
      ID:       uuid(),
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      FieldID:  fieldID,
      ResearchID: researchID,
      Categorie: '',
      Color:    secundairyColor,
      Position: (categories?.length || 0) + 1
    });
  };

  // ---- Option 1: Color Picker Change ----
  const handleColorChange = useCallback((e, catID) => {
    const docid    = e.target.dataset.docid;
    const newColor = e.target.value;

    // 1️⃣ Persist new color in Firestore
    db.collection("AnalysisCategories")
      .doc(docid)
      .update({ Color: newColor });

    // 2️⃣ If this is the active category, switch our state highlight color
    if (catID === categoryID) {
      setColor(newColor);
    }
  }, [categoryID]);

  // ========== Render ==========
  return (
    <div className="main">
      <div className="main-container">
        <PageHeader
          heading={"Categoriseer"}
          description={`Hier kun je de open vraag '${question}' categoriseren.`}
          AI={false}
        />

        {/* Moment selector */}
        <SelectableGrid
          items={moments || []}
          getId={(item) => item.ID}
          getLabel={(item) => item.Title}
          selectedId={momentID}
          onClick={setMomentID}
        />

        <ReadOnlyGuard edit="measurement.edit">
          {(canEdit) => (
            <>
              {/* Categories table */}
              <div className="page-content-container">
                <p><b>Categorien</b></p>
                <Table
                  columns={[
                    { key: 'select', label: 'SELECTEER', searchable: false },
                    { key: 'category', label: 'CATEGORIE' },
                    { key: 'words', label: 'GESELECTEERDE WOORDEN', searchable: false },
                    { key: 'color', label: 'KLEUR', searchable: false },
                    { key: 'delete', label: 'VERWIJDER', searchable: false },
                  ]}
                  items={categories || []}
                  getItemKey={(item) => item.ID}
                  renderRow={(item) => (
                    <>
                      <TableCell>
                        <input
                          type="radio"
                          name="selectCategory"
                          data-color={item.Color}
                          data-docid={item.docid}
                          value={item.ID}
                          checked={categoryID === item.ID}
                          onChange={selectCategory}
                        />
                      </TableCell>
                      <TableCell><CategoryAnalysisTitle item={item}/></TableCell>
                      <TableCell>
                        <CategoryWords
                          category={item}
                          field={fieldID}
                          moment={momentID}
                        />
                      </TableCell>
                      <TableCell>
                        <ColorPickerHousestyle
                          item={item}
                          onChange={e => handleColorChange(e, item.ID)}
                        />
                      </TableCell>
                      <TableCell>
                        {canEdit && (
                          <Tooltip content='Categorie verwijderen' top='-60px'>
                            <DeleteOutlinedIcon
                              className='table-delete-icon'
                              data-docid={item.docid}
                              onClick={deleteCategory}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                    </>
                  )}
                  searchAccessor={(item) => item?.Categorie || ''}
                  searchPlaceholder="Zoeken op categorie"
                  emptyMessage="Geen categorieen gevonden."
                  addRow={canEdit ? {
                    toolTipContent: 'Voeg een categorie toe',
                    onClick: addCategory,
                  } : undefined}
                />
              </div>

              {/* Responses */}
              <div className="table-container">
                <p><b>Responses</b></p>
                {results && results.map(r => (
                  <AnalyseResponse
                    key={r.ID}
                    item={r}
                    setSaved={setSaved}
                    categoryID={categoryID}
                    moment={momentID}
                    field={fieldID}
                    color={color}
                  />
                ))}
              </div>
            </>
          )}
        </ReadOnlyGuard>
      </div>
    </div>
  );
};

export default ResearchResultsOpenQuestionAnalysis;
